import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const FileTypeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    fileName: '',
    fileDescription: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchFileType();
    }
  }, [id]);

  const fetchFileType = async () => {
    const { data } = await get(`/api/file-types/${id}`);
    if (data) {
      setFormData({
        fileName: data.fileName || '',
        fileDescription: data.fileDescription || ''
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fileName.trim()) {
      newErrors.fileName = 'File name is required';
    }
    if (!formData.fileDescription.trim()) {
      newErrors.fileDescription = 'File description is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    let response;
    if (id) {
      response = await put(`/api/file-types/${id}`, formData);
    } else {
      response = await post('/api/file-types', formData);
    }

    if (!response.error) {
      navigate('/file-types');
    }
  };

  if (loading && id) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/file-types')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Edit File Type' : 'Add New File Type'}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fileName"
              value={formData.fileName}
              onChange={handleChange}
              className={`input-field ${errors.fileName ? 'border-red-500' : ''}`}
              placeholder="Enter file name"
            />
            {errors.fileName && (
              <p className="mt-1 text-sm text-red-600">{errors.fileName}</p>
            )}
          </div>

          {/* File Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="fileDescription"
              value={formData.fileDescription}
              onChange={handleChange}
              rows="4"
              className={`input-field ${errors.fileDescription ? 'border-red-500' : ''}`}
              placeholder="Enter file description"
            />
            {errors.fileDescription && (
              <p className="mt-1 text-sm text-red-600">{errors.fileDescription}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/file-types')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FileTypeForm;